import { facetValueToSlug } from '../../funnelback/deakin-config';
import {
  resolveFacetId,
  type FunnelbackFacetFilterFacet,
} from '../../components/facet-filter';
import { canonicalizeKnownFacetId, facetIdsEqual } from './facet-id';

function facetValuesMatch(a: string, b: string): boolean {
  return facetValueToSlug(a) === facetValueToSlug(b);
}

function facetDataValues(
  facet: FunnelbackFacetFilterFacet,
): string[] {
  const values = facet.allValues ?? [];
  return values.map((v) => v.data).filter(Boolean);
}

/**
 * Align selected facet values with Funnelback `data` labels after the first response.
 * Page metadata often stores slugs or human labels; facet UI uses Funnelback `data`.
 */
export function normalizeSelectedFacets(
  selectedFacets: Record<string, string[]>,
  funnelbackFacets: unknown[],
): Record<string, string[]> {
  if (!Array.isArray(funnelbackFacets) || funnelbackFacets.length === 0) {
    return selectedFacets;
  }

  const facets = funnelbackFacets as FunnelbackFacetFilterFacet[];
  const next: Record<string, string[]> = {};
  let changed = false;

  for (const [facetId, values] of Object.entries(selectedFacets)) {
    if (!values?.length) {
      continue;
    }
    const facet = facets.find((f) => {
      const resolvedId = resolveFacetId(f);
      return resolvedId !== null && facetIdsEqual(resolvedId, facetId);
    });
    const canonicalFacetId = facet
      ? (resolveFacetId(facet) ?? canonicalizeKnownFacetId(facetId))
      : canonicalizeKnownFacetId(facetId);
    const dataValues = facet ? facetDataValues(facet) : [];
    const normalized =
      dataValues.length === 0
        ? values
        : values.map((selected) => {
            const match = dataValues.find((data) =>
              facetValuesMatch(selected, data),
            );
            return match ?? selected;
          });
    if (
      !facetIdsEqual(canonicalFacetId, facetId) ||
      normalized.length !== values.length ||
      normalized.some((value, index) => value !== values[index])
    ) {
      changed = true;
    }
    next[canonicalFacetId] = normalized;
  }

  return changed ? next : selectedFacets;
}
